import { getUserMailboxInfo } from 'owa-client-ids';
import { ChatContactPersona } from './ChatContact';
import ChatContactList from './ChatContactList';
import getEmailAddressFromSipUri from '../../presenceManager/getEmailAddressFromSipUri';
import getMySipUri from '../../presenceManager/getMySipUri';
import getRecipientDisplayName from '../selectors/getRecipientDisplayName';
import ChatViewState from '../store/schema/ChatViewState';
import {
    contactSearchNoResults,
    dispatcherContactsHeader,
    dispatcherSearchHeader,
} from '../strings.locstring.json';
import { observer } from 'mobx-react-lite';
import { Icon } from '@fluentui/react/lib/Icon';
import { TextField } from '@fluentui/react/lib/TextField';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import processSubstrateSuggestionsResponse from 'owa-recipient-common/lib/actions/processSubstrateSuggestionsResponse';
import substrateSuggestions from 'owa-substrate-people-suggestions/lib/services/substrateSuggestions';
import * as React from 'react';
import {
    isEmailCacheHit,
    searchCacheWithFunction,
} from 'owa-recipient-cache/lib/selectors/searchCache';

const styles = require('./ChatDispatcher.scss');

const INITIAL_CONTACTS_TO_FETCH = 5;
const MAX_CONTACTS_TO_SHOW = 15;

export interface ChatDispatcherProps {
    currentlyActiveChats: ChatViewState[];
}

export default observer(function ChatDispatcher(props: ChatDispatcherProps) {
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const activeChats = React.useMemo(
        () => getChatViewStatesAsContactPersonas(props.currentlyActiveChats),
        [props.currentlyActiveChats]
    );

    const [displayedContacts, setDisplayedContacts] = React.useState([
        ...activeChats,
        ...searchCacheContacts('', activeChats),
    ]);

    React.useEffect(() => {
        let shouldIgnore = false;
        const filteredExistingChats = activeChats.filter(item =>
            isEmailCacheHit(searchQuery.toLowerCase())(item)
        );
        const startingContacts = [
            ...activeChats,
            ...searchCacheContacts(searchQuery, filteredExistingChats),
        ];
        setDisplayedContacts(startingContacts);

        async function fetchServer() {
            const completeContacts = await searchServerContacts(searchQuery, startingContacts);
            if (!shouldIgnore) {
                setDisplayedContacts(completeContacts);
            }
        }
        if (searchQuery.length > 0) {
            fetchServer();
        }
        return () => {
            shouldIgnore = true;
        };
    }, [searchQuery, activeChats]);

    const onChange = React.useCallback(
        (event: React.FormEvent<HTMLInputElement>, newValue: string) => {
            setSearchQuery(newValue);
        },
        []
    );

    return (
        <div className={styles.dispatcherBox}>
            <div className={styles.inputBox}>
                <Icon iconName={ControlIcons.Search} className={styles.searchIcon} />
                <TextField
                    type="text"
                    borderless={true}
                    placeholder={'Search'}
                    value={searchQuery}
                    onChange={onChange}
                    resizable={false}
                    styles={{
                        field: styles.inputField,
                        fieldGroup: styles.heightControl,
                        wrapper: styles.heightControl,
                        root: styles.inputRoot,
                    }}
                />
            </div>
            <div className={styles.headerContainer}>
                <span>
                    {searchQuery.length > 0
                        ? loc(dispatcherSearchHeader)
                        : loc(dispatcherContactsHeader)}
                </span>
            </div>
            <div className={styles.contactsContainer}>
                {displayedContacts.length > 0 ? (
                    <ChatContactList contacts={displayedContacts} />
                ) : (
                    <div className={styles.errorSearchResultsContainer}>
                        <span>{loc(contactSearchNoResults)}</span>
                    </div>
                )}
            </div>
        </div>
    );
});

function searchCacheContacts(
    searchQuery: string,
    chatContactsToFilter: ChatContactPersona[]
): ChatContactPersona[] {
    return searchCacheWithFunction(persona => {
        return (
            !!persona.ImAddress &&
            isEmailCacheHit(searchQuery.toLowerCase())(persona) &&
            persona.ImAddress != getMySipUri() &&
            !chatContactsToFilter.some(item => item.ImAddress == persona.ImAddress)
        );
    }, INITIAL_CONTACTS_TO_FETCH).map(item => {
        return {
            EmailAddress: item.EmailAddress,
            ImAddress: item.ImAddress,
        };
    });
}

async function searchServerContacts(
    searchQuery: string,
    currentChatContacts: ChatContactPersona[]
): Promise<ChatContactPersona[]> {
    const substrateResponse = await substrateSuggestions(
        getUserMailboxInfo().userIdentity,
        searchQuery,
        true /* includeDirectory */
    );
    const unfilteredChatContacts: ChatContactPersona[] = [];
    processSubstrateSuggestionsResponse(
        unfilteredChatContacts,
        substrateResponse,
        null /*correlationId*/,
        false
    );
    const substrateChatContacts = unfilteredChatContacts
        .filter(
            item =>
                item.EmailAddress &&
                item.ImAddress &&
                !currentChatContacts.some(check => check.ImAddress == item.ImAddress)
        )
        .map(item => {
            return {
                EmailAddress: item.EmailAddress,
                ImAddress: item.ImAddress,
            };
        });
    return currentChatContacts.concat(substrateChatContacts).slice(0, MAX_CONTACTS_TO_SHOW);
}

function getChatViewStatesAsContactPersonas(chats: ChatViewState[]): ChatContactPersona[] {
    return chats
        ? chats.map(chat => {
              return {
                  EmailAddress: {
                      EmailAddress: getEmailAddressFromSipUri(chat.participants[0]),
                      Name: getRecipientDisplayName(chat.participants[0]),
                  },
                  ImAddress: chat.participants[0],
                  linkedChatViewState: chat,
              };
          })
        : [];
}
